/** The numbers of a bout: its rounds and clock, blocking and dodging, being hit, and stamina. */
export interface FightRules {
  readonly rounds: number;
  readonly roundSeconds: number;
  readonly restSeconds: number;
  /** Share of the health lost so far that each fighter gets back in the rest between rounds. */
  readonly recoveryShare: number;
  /** Share of a punch's damage that goes through a block. */
  readonly blockShare: number;
  readonly dodgeSeconds: number;
  readonly dodgeRecoverySeconds: number;
  /** Seconds a clean hit keeps him from punching; he can still block and dodge. */
  readonly hitStunSeconds: number;
  /** Seconds a staggering punch keeps him from doing anything. */
  readonly staggerSeconds: number;
  /** Seconds after a stagger in which he cannot be staggered again. */
  readonly staggerGuardSeconds: number;
  /** Clean hits that charge the super punch. */
  readonly superHits: number;
  readonly staminaMax: number;
  readonly staminaPerSecond: number;
  /** Seconds after a punch before stamina starts coming back. */
  readonly staminaDelaySeconds: number;
  /** How much of the stamina comes back while blocking. */
  readonly staminaWhileBlocking: number;
}

export const defaultRules: FightRules = {
  rounds: 3,
  roundSeconds: 60,
  restSeconds: 5,
  recoveryShare: 0.25,
  blockShare: 0.2,
  dodgeSeconds: 0.4,
  dodgeRecoverySeconds: 0.3,
  hitStunSeconds: 0.25,
  staggerSeconds: 0.45,
  staggerGuardSeconds: 2,
  superHits: 6,
  staminaMax: 100,
  staminaPerSecond: 20,
  staminaDelaySeconds: 0.6,
  staminaWhileBlocking: 0.5,
};

export function fightRules(changes: Partial<FightRules> = {}): FightRules {
  const rules = { ...defaultRules, ...changes };
  if (rules.rounds < 1) throw new RangeError('A bout has at least one round');
  if (rules.roundSeconds <= 0 || rules.dodgeSeconds <= 0) throw new RangeError('A round and a dodge must last longer than zero seconds');
  for (const key of ['restSeconds', 'dodgeRecoverySeconds', 'hitStunSeconds', 'staggerSeconds', 'staggerGuardSeconds', 'staminaDelaySeconds'] as const)
    if (rules[key] < 0) throw new RangeError(`${key} cannot be negative`);
  for (const key of ['recoveryShare', 'blockShare', 'staminaWhileBlocking'] as const)
    if (rules[key] < 0 || rules[key] > 1) throw new RangeError(`${key} goes from 0 to 1`);
  if (rules.superHits < 1) throw new RangeError('The super punch needs at least one hit');
  if (rules.staminaMax <= 0 || rules.staminaPerSecond < 0) throw new RangeError('Stamina needs a size and cannot drain by itself');
  return rules;
}
